<template>
  <v-form v-model="valid" ref="form" @submit.prevent="create">
    <v-snackbar
      top
      :color="snackbar.color"
      v-model="snackbar.model"
      timeout="1000"
      >{{ snackbar.msg }}</v-snackbar
    >
    <v-row>
      <!-- title -->
      <v-col cols="12">
        <label v-bind="labelProps">Title</label>
        <v-text-field
          v-model="form.title"
          :rules="[rules.required]"
          v-bind="fieldProps"
        ></v-text-field>
      </v-col>

      <!-- start date -->
      <v-col cols="12" md="6">
        <label v-bind="labelProps">Start date</label>

        <v-menu
          ref="startDateMenu"
          v-model="startDateMenu"
          :close-on-content-click="false"
          :return-value.sync="startDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              readonly
              v-bind="{ attrs, ...fieldProps }"
              v-on="on"
              :rules="[rules.required]"
            ></v-text-field>
          </template>
          <v-date-picker v-model="startDate" scrollable :min="dateNow">
            <v-spacer></v-spacer>

            <v-btn
              text
              color="primary"
              @click="$refs.startDateMenu.save(startDate)"
            >
              select
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>

      <!-- start time -->
      <v-col cols="12" md="6">
        <label v-bind="labelProps">Start time</label>

        <v-menu
          v-model="startTimeMenu"
          :close-on-content-click="false"
          :return-value.sync="startTime"
          transition="scale-transition"
          offset-y
          min-width="auto"
          ref="startTimeMenu"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startTime"
              readonly
              v-bind="{ attrs, ...fieldProps }"
              v-on="on"
              :rules="[rules.required]"
            ></v-text-field>
          </template>
          <v-time-picker
            v-model="startTime"
            scrollable
            @click:minute="$refs.startTimeMenu.save(startTime)"
          >
          </v-time-picker>
        </v-menu>
      </v-col>

      <!-- end date -->
      <v-col cols="12" md="6">
        <label v-bind="labelProps">End date</label>

        <v-menu
          ref="endDateMenu"
          v-model="endDateMenu"
          :close-on-content-click="false"
          :return-value.sync="endDate"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              label="End date"
              readonly
              v-bind="{ attrs, ...fieldProps }"
              v-on="on"
              :rules="[rules.required]"
            ></v-text-field>
          </template>
          <v-date-picker v-model="endDate" scrollable :min="dateNow">
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="$refs.endDateMenu.save(endDate)"
            >
              select
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>

      <!-- end time -->
      <v-col cols="12" md="6">
        <label v-bind="labelProps">End time</label>

        <v-menu
          v-model="endTimeMenu"
          :close-on-content-click="false"
          :return-value.sync="endTime"
          transition="scale-transition"
          offset-y
          min-width="auto"
          ref="endTimeMenu"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endTime"
              readonly
              v-bind="{ attrs, ...fieldProps }"
              v-on="on"
              :rules="[rules.required]"
            ></v-text-field>
          </template>
          <v-time-picker
            v-model="endTime"
            scrollable
            @click:minute="$refs.endTimeMenu.save(endTime)"
          >
          </v-time-picker>
        </v-menu>
      </v-col>

      <!-- publish mode -->
      <v-col cols="12" :md="form.publish_mediums.includes('sms') ? 6 : 12">
        <label v-bind="labelProps">Publish medium</label>
        <v-select
          v-model="form.publish_mediums"
          :rules="[rules.multiple]"
          :items="['email', 'sms']"
          small-chips
          :readonly="$route.name == 'Manage' && true"
          v-bind="fieldProps"
          multiple
        >
        </v-select>
      </v-col>

      <v-col cols="12" md="6" v-if="form.publish_mediums.includes('sms')">
        <label v-bind="labelProps"
          >SMS subject ({{ 11 - form.sms_subject.length }} left)</label
        >
        <v-text-field
          :readonly="$route.name == 'Manage' && true"
          v-model="form.sms_subject"
          :maxlength="11"
          :rules="[rules.required]"
          v-bind="fieldProps"
        >
        </v-text-field>
      </v-col>

      <!-- description -->
      <v-col cols="12">
        <label v-bind="labelProps">Description</label>
        <v-textarea
          v-model="form.description"
          v-bind="fieldProps"
          rows="3"
        ></v-textarea>
      </v-col>

      <v-col cols="12" :class="{ 'd-flex justify-end my-3': !largeBtn }">
        <v-btn
          :loading="loading"
          :disabled="loading"
          type="submit"
          v-bind="btnProps"
          :block="largeBtn"
          class="primary"
        >
          {{ largeBtn ? "Create poll" : "Save" }}</v-btn
        >
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { rules, fieldProps, btnProps, labelProps } from "@/helpers";
import { mapActions } from "vuex";

export default {
  props: {
    largeBtn: {
      type: Boolean,
      default: true,
    },
    prepData: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    rules,
    fieldProps,
    btnProps,
    labelProps,

    snackbar: {
      model: false,
      msg: "Error!",
      color: "error",
    },

    form: {
      title: "",
      start_at: "",
      end_at: "",
      description: "",
      publish_mediums: [],
      sms_subject: "",
    },

    loading: false,
    show: false,
    valid: true,

    dateNow: null,
    timeNow: null,

    startDate: null,
    startTime: "",

    endDate: null,
    endTime: "",

    startDateMenu: false,
    endDateMenu: false,
    startTimeMenu: false,
    endTimeMenu: false,
  }),

  computed: {
    poll() {
      return this.$store.getters.getCollection("poll");
    },
  },

  methods: {
    ...mapActions(["userAuth", "makeFetch", "updateCollection"]),

    showSnackbar(msg, color) {
      this.snackbar.model = true;
      this.snackbar.msg = msg;
      this.snackbar.color = color;
    },

    async create() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      const startAt = new Date(`${this.startDate} ${this.startTime}`);
      const endAt = new Date(`${this.endDate} ${this.endTime}`);

      const pollId = this.$route.params.id;
      const data = { ...this.form, start_at: startAt, end_at: endAt };

      // creating
      const createReq = {
        method: "post",
        goTo: "/polls",
        stateVar: "polls",
        data: { ...data },
      };

      // updating
      const updateReq = {
        method: "put",
        goTo: `/polls/${pollId}`,
        data: { ...data },
      };

      const res = await this.makeFetch(
        pollId ? { ...updateReq } : { ...createReq }
      )
        .catch((err) => {
          this.showSnackbar(err.message, "error");
        })
        .finally(() => {
          this.loading = false;
        });

      if (!res) {
        this.showSnackbar("Connection error!", "error");
        return;
      }

      if (pollId) {
        if (res.data.message) this.showSnackbar(res.data.message, "error");
        else {
          this.showSnackbar("Saved", "primary");
          this.updateCollection({
            cn: "poll",
            data: res.data,
          });
          this.$router.go();
        }
      } else {
        this.$emit("poll-created", { data: res.data });
      }
    },
  },

  async mounted() {
    const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    const dateNow = now.toISOString().substring(0, 10);

    this.dateNow = dateNow;
    this.startDate = dateNow;
    this.endDate = dateNow;

    if (this.$route.params.id) {
      await this.makeFetch({
        method: "get",
        goTo: `/polls/${this.$route.params.id}`,
        stateVar: "poll",
      }).catch(console.error);
    }
  },

  watch: {
    poll(val) {
      if (!val.title) return;

      // prepopulate
      this.form = { ...this.form, ...val };

      this.startDate = this.form.start_at.substr(0, 10);
      this.endDate = this.form.end_at.substr(0, 10);
      this.startTime = this.form.start_at.substr(11, 5);
      this.endTime = this.form.end_at.substr(11, 5);
    },
  },
};
</script>
